import type { DrawObject, MeasureView, View } from "@novorender/api";
import type { BoundingSphere } from "@novorender/data-js-api";
import { type ReadonlyVec3, vec3 } from "gl-matrix";
import pMap from "p-map";
import { useSyncExternalStore } from "react";

import type { CameraState } from "features/engine2D";
import { CameraType } from "features/render/types";

/**
 * Whether the measure worker has a BREP for the object, through its cache.
 * @throws If the worker fails to read one it has.
 */
export async function hasBrep(view: View, objectId: number): Promise<boolean> {
    return (await view.measure?.core.getParametricProduct(objectId)) !== undefined;
}

/**
 * Saves the BREP as the measure worker parsed it, which re-serializes the JSON.
 * @throws If the worker has no BREP for the object.
 */
export async function downloadBrep(view: View, objectId: number) {
    const brep = await view.measure?.worker.downloadBrep(objectId);
    if (!brep?.product) {
        throw new Error("The measure worker could not download the BREP");
    }
    const url = URL.createObjectURL(new Blob([JSON.stringify(brep.product)], { type: "application/json" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `${objectId}.json`;
    // Firefox only downloads from an anchor in the document, and only while the URL is alive.
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Worker calls in flight at once, so one large BREP doesn't starve measure. */
const WORKER_CONCURRENCY = 8;
/** Points kept across all edges, and across all faces, so reprojecting on camera moves stays cheap. */
const MAX_POINTS = 50_000;

let topologyCache: { measure: MeasureView; objectId: number; topology: ReturnType<typeof loadTopology> } | undefined;

/**
 * Each instance's faces and edges, read through the worker's BREP cache and kept for the last object
 * asked for. `getParametricProduct` is `@ignore` in the API but, unlike `downloadBrep`, doesn't
 * download the BREP again.
 * @throws If the worker has no BREP for the object, including a failed load it has cached.
 */
function getTopology(view: View, objectId: number) {
    const measure = view.measure;
    if (!measure) {
        return Promise.reject(new Error("The scene has no measure view"));
    }
    if (topologyCache?.measure !== measure || topologyCache.objectId !== objectId) {
        const topology = loadTopology(measure, objectId);
        topologyCache = { measure, objectId, topology };
        topology.catch(() => {
            if (topologyCache?.topology === topology) {
                topologyCache = undefined;
            }
        });
    }
    return topologyCache.topology;
}

async function loadTopology(measure: MeasureView, objectId: number) {
    const product = await measure.core.getParametricProduct(objectId);
    if (!product) {
        throw new Error("The measure worker has no BREP for this object");
    }
    const instances = product.geometries.map(({ index, solids }) => {
        const faces = solids.flatMap((solid) => [solid.outerShell, ...(solid.innerShells ?? [])]).flat();
        const edges = new Set(
            faces
                .flatMap((face) => [face.outerLoop, ...(face.innerLoops ?? [])])
                .flatMap((loop) => loop.map((edge) => edge.index)),
        );
        return { instanceIdx: index, faces: faces.map((face) => face.index), edges: [...edges] };
    });
    return { measure, instances };
}

/**
 * @returns World-space polyline of every BREP edge of every instance of the object, thinned to
 * {@link MAX_POINTS} in total.
 * @throws If the worker has none, or `signal` aborts.
 */
export async function getBrepEdges(view: View, objectId: number, signal?: AbortSignal): Promise<ReadonlyVec3[][]> {
    const { measure, instances } = await getTopology(view, objectId);
    const segments = await pMap(
        instances.flatMap(({ instanceIdx, edges }) => edges.map((edgeIdx) => ({ instanceIdx, edgeIdx }))),
        ({ instanceIdx, edgeIdx }) => measure.worker.getTesselatedEdge(objectId, edgeIdx, instanceIdx),
        { concurrency: WORKER_CONCURRENCY, signal },
    );
    const edges = segments.filter((points) => points.length > 1);
    if (edges.length === 0) {
        throw new Error("The measure worker returned no edges");
    }
    return decimate(edges, MAX_POINTS);
}

/** Keeps every n-th point of each polyline, and its last, so the total is about `maxPoints`. */
export function decimate(polylines: ReadonlyVec3[][], maxPoints: number): ReadonlyVec3[][] {
    const total = polylines.reduce((sum, points) => sum + points.length, 0);
    const stride = Math.ceil(total / maxPoints);
    if (stride <= 1) {
        return polylines;
    }
    return polylines.map((points) => points.filter((_, i) => i % stride === 0 || i === points.length - 1));
}

/**
 * @returns Sphere around the BREP's own edges, which may sit apart from the rendered object.
 * @throws If the edges have no finite extent past the camera's near plane, which zooming would fly inside.
 */
export async function getBrepBoundingSphere(view: View, objectId: number): Promise<BoundingSphere> {
    const points = (await getBrepEdges(view, objectId)).flat().filter((point) => point.every(Number.isFinite));
    if (points.length === 0) {
        throw new Error("The BREP has no finite points");
    }
    const min = vec3.clone(points[0]);
    const max = vec3.clone(points[0]);
    for (const point of points) {
        vec3.min(min, min, point);
        vec3.max(max, max, point);
    }
    const center = vec3.lerp(vec3.create(), min, max, 0.5);
    const radius = vec3.distance(min, max) / 2;
    if (!(radius >= view.renderState.camera.near)) {
        throw new Error("The BREP is too small to zoom to");
    }
    return { center, radius };
}

/**
 * @returns World-space fill of every BREP face of every instance of the object, until
 * {@link MAX_POINTS} is reached. Cylinders have none.
 * @throws If `signal` aborts.
 */
export async function getBrepFaces(view: View, objectId: number, signal?: AbortSignal): Promise<DrawObject[]> {
    const { measure, instances } = await getTopology(view, objectId);
    const faces = await pMap(
        instances.flatMap(({ instanceIdx, faces }) => faces.map((faceIdx) => ({ instanceIdx, faceIdx }))),
        ({ instanceIdx, faceIdx }) => measure.worker.getFaceDrawObject(objectId, faceIdx, instanceIdx),
        { concurrency: WORKER_CONCURRENCY, signal },
    );
    const filled: DrawObject[] = [];
    let points = 0;
    for (const face of faces) {
        // Planar faces also carry corner `angle` parts, cylinders an axis `lines` part.
        const parts = face?.parts.filter((part) => part.drawType === "filled") ?? [];
        points += parts.reduce((sum, part) => sum + part.vertices3D.length, 0);
        if (points > MAX_POINTS) {
            console.warn(`Drawing ${filled.length} of ${faces.length} BREP faces`);
            break;
        }
        if (face && parts.length > 0) {
            filled.push({ ...face, parts });
        }
    }
    return filled;
}

/**
 * Whether the outer loop winds counter-clockwise as seen from the camera, which the BREP's outward
 * normal implies for a face toward it. Taken in world space, since the screen outline is clipped at
 * the near plane. Only planar faces are culled; an edge-on face counts as back.
 */
export function isFrontFacing(face: DrawObject, camera: CameraState) {
    const loop = face.parts[0]?.vertices3D;
    if (face.kind !== "plane" || !loop || loop.length < 3) {
        return true;
    }
    const toCamera =
        camera.type === CameraType.Orthographic
            ? vec3.negate(vec3.create(), camera.dir)
            : vec3.sub(vec3.create(), camera.pos, loop[0]);
    return vec3.dot(newellNormal(loop), toCamera) > 0;
}

/** @returns Normal of a planar loop, scaled by its area, pointing the way it winds counter-clockwise around. */
function newellNormal(loop: ReadonlyVec3[]) {
    const normal = vec3.create();
    for (let i = 0; i < loop.length; i++) {
        const [ax, ay, az] = loop[i];
        const [bx, by, bz] = loop[(i + 1) % loop.length];
        normal[0] += (ay - by) * (az + bz);
        normal[1] += (az - bz) * (ax + bx);
        normal[2] += (ax - bx) * (ay + by);
    }
    return normal;
}

export type BrepDisplay = {
    /** Draws edges, and gates the other options. */
    show: boolean;
    /** Angles between consecutive segments of each edge. */
    angles: boolean;
    surfaces: boolean;
    /** Fill planar faces facing away from the camera too, in their own color. */
    backFaces: boolean;
};

let brepDisplay: BrepDisplay = { show: false, angles: false, surfaces: true, backFaces: true };
const listeners = new Set<() => void>();

export function setBrepDisplay(patch: Partial<BrepDisplay>) {
    brepDisplay = { ...brepDisplay, ...patch };
    listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

export function useBrepDisplay() {
    return useSyncExternalStore(subscribe, () => brepDisplay);
}
